import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Picker from 'emoji-picker-react';
import EncryptionBanner from './EncryptionBanner';
import ChatsSkeleton from '../../components/Skeletons/ChatsSkeleton';
import ChatStyles from './Chat.module.css';

const SelectedChat = ({
  chatUserLoading,
  encryptedSecurityKey,
  selectedUser,
  showSecurityKey,
  handleMakeDefaultKey,
  setShowSecurityKey,
  setSecurityKeyModal,
  encryptedChatData,
  decryptedChatData,
  messagesStartRef,
  securityKeys,
  getMoreChats,
  groupedData,
  userLogin,
  lastTopMessage,
  topMessageRef,
  setSelectedMessage,
  setShowReactBox,
  showReactBox,
  reactToChat,
  messagesEndRef,
  removedGroup,
  chatText,
  selectedMessage,
  fileInputRef,
  setAttachedFiles,
  setShowEmojiBox,
  showEmojiBox,
  attachedFiles,
  setChatText,
  socket,
  chatUser,
  onEmojiClick,
  chatSendLoading,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {/* Encryption banner */}
      {!chatUserLoading && encryptedSecurityKey?.status === 'success' && <EncryptionBanner encryptedSecurityKey={encryptedSecurityKey} selectedUser={selectedUser} />}

      {/* Security key management view */}
      {!chatUserLoading && showSecurityKey ? (
        <div className={ChatStyles.chatBox}>
          <div ref={messagesStartRef} />
          <div style={{ minHeight: 'calc(60vh - 8rem)' }}>
            {securityKeys?.data?.length > 0 &&
              securityKeys.data.map(key => (
                <div className={ChatStyles.chatBox_key} key={key._id}>
                  <h5>{key.key}</h5>
                  {key.default ? <p>{t('screens.chat.usingAsDefault')}</p> : <button onClick={() => handleMakeDefaultKey(key._id)}>{t('screens.chat.makeDefault')}</button>}
                </div>
              ))}
          </div>
          <div className={ChatStyles.chatBox_key_add}>
            <button onClick={() => setShowSecurityKey(false)}>{t('screens.chat.goBack')}</button>
            <button onClick={() => setSecurityKeyModal(true)}>{t('screens.chat.newKey')}</button>
          </div>
        </div>
      ) : (
        /* Messages area */
        <div className={ChatStyles.chatBox}>
          <p className={ChatStyles.chatBox_msg}>{t('screens.chat.messagesEncrypted')}</p>
          {encryptedChatData?.has_more && (
            <div className={ChatStyles.chatBox_loadMore}>
              <button className={ChatStyles.chatBox_loadMoreBtn} onClick={getMoreChats}>
                {t('screens.chat.loadMoreChats')}
              </button>
            </div>
          )}

          <div className={ChatStyles.chatBox_chats}>
            {chatUserLoading ? (
              <ChatsSkeleton />
            ) : decryptedChatData?.length > 0 ? (
              groupedData &&
              Object.keys(groupedData).length > 0 &&
              Object.keys(groupedData).map(
                key =>
                  groupedData[key].length > 0 &&
                  (groupedData[key].push(),
                  (
                    <Fragment key={key}>
                      <p className={ChatStyles.chatBox_date}>
                        {key === new Date(Date.now()).toDateString() ? t('screens.chat.today') : key === new Date(Date.now() - 86400000).toDateString() ? t('screens.chat.yesterday') : key}
                      </p>
                      {groupedData[key].map((chat, chatIndex) => {
                        const myId = userLogin?.userInfo?.data?.user?._id;
                        const myName = userLogin?.userInfo?.data?.user?.name;
                        return chat.isEncryptedMessage ? (
                          <div
                            key={chat._id}
                            ref={lastTopMessage && Object.keys(lastTopMessage).length > 0 && lastTopMessage._id === chat._id ? topMessageRef : null}
                            className={myId === chat.senderId ? ChatStyles.chatBox_out_chat : ChatStyles.chatBox_inc_chat}
                          >
                            <div className={ChatStyles.chatBox_chat_msgs}>
                              {myId === chat.senderId && (
                                <button className={ChatStyles.chatBox_chat_inc_msg_reply} onClick={() => setSelectedMessage(chat)}>
                                  <svg>
                                    <use xlinkHref={`/assets/sprite.svg#icon-reply`} />
                                  </svg>
                                </button>
                              )}

                              <div className={myId === chat.senderId ? ChatStyles.chatBox_chat_out_msg : ChatStyles.chatBox_chat_inc_msg}>
                                {chat.ToReplayMessage && (
                                  <div className={ChatStyles.chat_reply}>
                                    <div>
                                      <p className={ChatStyles.chat_reply_msg}>
                                        {chat.ToReplayMessage.messageText.split('\n').map((m, i) => (
                                          <span key={i} style={{ display: 'block' }}>
                                            {m}
                                          </span>
                                        ))}
                                      </p>
                                      <p className={ChatStyles.chat_reply_msg_name}>
                                        {chat.ToReplayMessage.senderName + ' at ' + new Date(chat.ToReplayMessage.messageTime).toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {chat.senderName !== myName && <span className={ChatStyles.senderName}>{chat.senderName}</span>}
                                {chat.messageText.split('\n').map((m, i) => (
                                  <span key={i} style={{ display: 'block' }}>
                                    {m}
                                  </span>
                                ))}
                                {chat.reaction?.length > 0 && (
                                  <div className={ChatStyles.chat_reply_reaction}>
                                    {chat.reaction.map((react, i) => (
                                      <p key={i}>{react.react || ''}</p>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {myId !== chat.senderId && (
                                <button
                                  className={ChatStyles.chatBox_chat_inc_msg_smiley}
                                  onClick={() =>
                                    setShowReactBox({
                                      status: !showReactBox.status,
                                      chat,
                                    })
                                  }
                                >
                                  <svg>
                                    <use xlinkHref={`/assets/sprite.svg#icon-emoji-happy`} />
                                  </svg>
                                </button>
                              )}

                              {myId !== chat.senderId && (
                                <button className={ChatStyles.chatBox_chat_inc_msg_reply} onClick={() => setSelectedMessage(chat)}>
                                  <svg>
                                    <use xlinkHref={`/assets/sprite.svg#icon-reply`} />
                                  </svg>
                                </button>
                              )}
                            </div>

                            <p className={ChatStyles.chatBox_chat_time}>{new Date(chat.messageTime).toLocaleTimeString().replace(/(.*)\D\d+/, '$1')}</p>

                            {myId !== chat.senderId && showReactBox.status && showReactBox.chat?._id === chat._id && (
                              <div className={ChatStyles.chatBox_chat_react}>
                                <button onClick={() => reactToChat('👍', chat.messageId)}>👍</button>
                                <button onClick={() => reactToChat('❤️', chat.messageId)}>❤️</button>
                                <button onClick={() => reactToChat('😊', chat.messageId)}>😊</button>
                                <button onClick={() => reactToChat('🙏', chat.messageId)}>🙏</button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className={ChatStyles.chatBox_msg} key={chat._id}>
                            {chat.messageText + ' at ' + new Date(chat.messageTime).toLocaleTimeString().replace(/(.*)\D\d+/, '$1')}
                          </p>
                        );
                      })}
                    </Fragment>
                  )),
              )
            ) : (
              <p className={ChatStyles.noChats}>{t('screens.chat.noChats')}</p>
            )}
          </div>
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Removed from group notice */}
      {removedGroup && <div className={ChatStyles.removedText}>{t('screens.chat.removedFromGroup')}</div>}

      {/* Compose area */}
      {!showSecurityKey && !removedGroup && (
        <div className={chatText.error ? ChatStyles.chat_send_errr : ChatStyles.chat_send}>
          {/* Reply preview */}
          {selectedMessage?.senderId && (
            <div className={ChatStyles.chat_reply}>
              <div>
                <p className={ChatStyles.chat_reply_msg}>{selectedMessage.messageText}</p>
                <p className={ChatStyles.chat_reply_msg_name}>
                  {userLogin?.userInfo?.data?.user?._id === selectedMessage.senderId ? t('screens.chat.you') : selectedMessage.senderName} at{' '}
                  {new Date(selectedMessage.messageTime).toLocaleString()}
                </p>
              </div>
              <button className={ChatStyles.chat_reply_close} onClick={() => setSelectedMessage({})}>
                <svg>
                  <use xlinkHref={`/assets/sprite.svg#icon-close`} />
                </svg>
              </button>
            </div>
          )}

          {/* Compose toolbar */}
          <div className={ChatStyles.composeToolbar}>
            <button className={ChatStyles.toolBtn} title={t('screens.chat.attachFile')}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
              </svg>
            </button>
            <button className={ChatStyles.toolBtn} title={t('screens.chat.securityKeys')} onClick={() => setShowSecurityKey(!showSecurityKey)}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                <rect x='3' y='11' width='18' height='11' rx='2' />
                <path d='M7 11V7a5 5 0 0 1 10 0v4' />
              </svg>
            </button>
            <span className={ChatStyles.toolDiv} />
            <button className={ChatStyles.toolBtn} title={t('screens.chat.attachFile2')} onClick={() => fileInputRef.current?.click()}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21.5 2v19c0 1.1-.9 2-2 2H4.5c-1.1 0-2-.9-2-2V2c0-1.1.9-2 2-2h15c1.1 0 2 .9 2 2z' />
                <line x1='7' y1='7' x2='17' y2='7' />
                <line x1='7' y1='12' x2='17' y2='12' />
                <line x1='7' y1='17' x2='14' y2='17' />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              style={{ display: 'none' }}
              accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt'
              onChange={e => {
                if (e.target.files?.length > 0) {
                  setAttachedFiles(prev => [...prev, ...Array.from(e.target.files)]);
                  e.target.value = '';
                }
              }}
            />
            <span className={ChatStyles.toolDiv} />
            <button className={ChatStyles.toolBtn} title={t('screens.chat.emoji')} onClick={() => setShowEmojiBox(!showEmojiBox)}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                <circle cx='12' cy='12' r='10' />
                <path d='M8 14s1.5 2 4 2 4-2 4-2' />
                <line x1='9' y1='9' x2='9.01' y2='9' />
                <line x1='15' y1='9' x2='15.01' y2='9' />
              </svg>
            </button>
          </div>

          {/* Attached files preview */}
          {attachedFiles.length > 0 && (
            <div style={{ padding: '0 16px 8px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {attachedFiles.map((file, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>
                  <span>{file.name.slice(0, 20)}</span>
                  <button
                    onClick={() => setAttachedFiles(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className={ChatStyles.composeRow}>
            <div className={ChatStyles.chat_send_textBox}>
              <textarea
                placeholder={t('screens.chat.writeSecureMessage')}
                className={ChatStyles.chat_send_text}
                value={chatText.value}
                onChange={e => {
                  setChatText({ value: e.target.value });
                  // Emit typing event
                  if (socket && selectedUser && selectedUser._id) {
                    socket.emit('userTyping', {
                      chatroomId: selectedUser.channelID,
                      userId: selectedUser._id,
                      isTyping: e.target.value.length > 0,
                    });
                  }
                }}
                onKeyPress={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    chatUser();
                  }
                }}
              />
              <div className={ChatStyles.composeEncLabel}>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <rect x='3' y='11' width='18' height='11' rx='2' />
                  <path d='M7 11V7a5 5 0 0 1 10 0v4' />
                </svg>
                {t('screens.chat.encryptedLabel')}
              </div>
              <div className={ChatStyles.chat_send_smiley_container}>
                {showEmojiBox && (
                  <div className={ChatStyles.chatBox_chat_emoji}>
                    <button className={ChatStyles.chat_reply_close} onClick={() => setShowEmojiBox(false)}>
                      <svg>
                        <use xlinkHref={`/assets/sprite.svg#icon-close`} />
                      </svg>
                    </button>
                    <Picker onEmojiClick={onEmojiClick} pickerStyle={{ boxShadow: 'none', border: 'none' }} />
                  </div>
                )}
              </div>
            </div>
            {chatSendLoading ? (
              <button className={ChatStyles.chat_send_message_loading}>
                <div className='dot-stretching' />
              </button>
            ) : (
              <button className={ChatStyles.chat_send_message} onClick={chatUser}>
                <svg>
                  <use xlinkHref={`/assets/sprite.svg#icon-send`} />
                </svg>
              </button>
            )}
          </div>

          {/* Compose footer */}
          <div className={ChatStyles.composerFooter}>
            <span className={ChatStyles.composerFooterEnc}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <rect x='3' y='11' width='18' height='11' rx='2' />
                <path d='M7 11V7a5 5 0 0 1 10 0v4' />
              </svg>
              {t('screens.chat.composerFooterEnc')}
            </span>
            <span>{t('screens.chat.composerReturnToSend')}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default SelectedChat;
